// runtime-overlay-vertical.js — the 9:16 (Reels/Shorts) broadcast overlay
// renderer. Pure canvas drawing: reads match state from the Svelte stores and
// paints one frame. Split from runtime-core.js.
import { activeMatch, lastBallState } from './svelte/store.js';
import {
  currentInnings, currentOverBalls, formatOvers, getMatchPhase
} from './scoring-logic.js';
import { getOverlayTheme, getOverlayLogoSrc } from './broadcast/overlay-themes.js';
import { getCachedLogo } from './runtime-cache.js';
import { withCtx, rgba, clipText, drawBallPill, roundRect } from './runtime-draw-utils.js';
import { teamObj, teamColor, battingTeamLetter, bowlingTeamLetter } from './runtime-teams.js';

let _activeMatch = null;
let _lastBallState = null;
activeMatch.subscribe(value => { _activeMatch = value; });
lastBallState.subscribe(value => { _lastBallState = value; });

export function drawVerticalOverlay(ctx, W, H) {
  const m = _activeMatch;
  if (!m || !m.innings || m.innings.length === 0) return;
  const inn = currentInnings(m);
  if (!inn) return;

  const battingTeam = teamObj(battingTeamLetter(inn));
  const bowlingTeam = teamObj(bowlingTeamLetter(inn));
  const battingColor = teamColor(battingTeam.name);
  const bowlingColor = teamColor(bowlingTeam.name);
  const striker = inn.batsmen.find(b => b.name === inn.currentStriker);
  const bowler = inn.bowlers.find(b => b.name === inn.currentBowler);
  const target = (m.innings.length === 2) ? m.innings[0].totalRuns + 1 : null;
  const challengeConfig = m.mode === 'challenge' && m.challenge?.type !== 'super_over' ? m.challenge : null;
  const theme = getOverlayTheme();
  const accent = theme.accent;

  // ═══ FREE HIT pill (top-center, pulsing) ═══
  if (inn.freeHit) {
    withCtx(ctx, () => {
      const t = Date.now() / 600;
      ctx.globalAlpha = 0.78 + 0.22 * Math.sin(t);
      const fhW = 200, fhH = 40;
      ctx.shadowColor = "rgba(234,88,12,0.7)";
      ctx.shadowBlur = 22;
      ctx.fillStyle = "#ea580c";
      roundRect(ctx, (W - fhW) / 2, 30, fhW, fhH, 20); ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.fillStyle = "#fff";
      ctx.font = "900 16px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("\u25cf FREE HIT \u25cf", W / 2, 30 + fhH / 2);
    });
  }

  // ═══ TOP BAR: Team ticker + Match Phase ═══
  withCtx(ctx, () => {
    const tx = 20, ty = 44, th = 38;
    const label = battingTeam.name.toUpperCase().slice(0, 12) + " vs " + bowlingTeam.name.toUpperCase().slice(0, 12);
    ctx.font = "900 15px Outfit, sans-serif";
    const tw = ctx.measureText(label).width + 40;
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    roundRect(ctx, tx, ty, tw, th, 10); ctx.fill();
    ctx.fillStyle = battingColor;
    roundRect(ctx, tx, ty, 6, th, 10); ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(label, tx + 20, ty + th / 2 + 1);
  });

  // Match phase badge (below ticker)
  if (theme.phaseBadge) {
    withCtx(ctx, () => {
      const phase = getMatchPhase(m.overs, inn.balls);
      const tx = 20, ty = 88, th = 26;
      const phaseLabel = phase.icon + " " + phase.name;
      ctx.font = "900 12px sans-serif";
      const tw = ctx.measureText(phaseLabel).width + 22;
      const pulse = phase.name === "DEATH OVERS" ? 0.85 + 0.15 * Math.sin(Date.now() / 300) : 1;
      ctx.globalAlpha = pulse;
      ctx.fillStyle = rgba(phase.color, 0.88);
      roundRect(ctx, tx, ty, tw, th, 6); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      ctx.fillText(phaseLabel, tx + 11, ty + th / 2 + 1);
    });
  }

  // ═══ WATERMARK (top-right) — customizable / hideable for PRO ═══
  const vLogoSrc = getOverlayLogoSrc();
  const watermark = vLogoSrc ? getCachedLogo(vLogoSrc) : null;
  if (watermark) {
    withCtx(ctx, () => {
      ctx.globalAlpha = 0.8;
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 10;
      ctx.drawImage(watermark, W - 90, 40, 65, 65);
    });
  }

  // ═══ MAIN STACKED SCORECARD (bottom) ═══
  withCtx(ctx, () => {
    const stripW = W - 24;
    const stripH = 155;
    const stripY = H - stripH - 12;
    const stripX = 12;

    // ── Glass background ──
    const bgGrad = ctx.createLinearGradient(stripX, stripY, stripX, stripY + stripH);
    bgGrad.addColorStop(0, "rgba(8, 12, 22, 0.75)");
    bgGrad.addColorStop(1, "rgba(15, 23, 42, 0.85)");
    ctx.fillStyle = bgGrad;
    roundRect(ctx, stripX, stripY, stripW, stripH, 14); ctx.fill();
    
    // Glass border
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1;
    roundRect(ctx, stripX, stripY, stripW, stripH, 14); ctx.stroke();

    // ── Team color gradient top accent ──
    if (theme.topLine) {
      const gTop = ctx.createLinearGradient(stripX, stripY, stripX + stripW, stripY);
      gTop.addColorStop(0, theme.teamPanel ? battingColor : accent);
      gTop.addColorStop(1, theme.teamPanel ? bowlingColor : accent);
      ctx.fillStyle = gTop;
      ctx.save();
      roundRect(ctx, stripX, stripY, stripW, stripH, 14); ctx.clip();
      ctx.fillRect(stripX, stripY, stripW, 3);
      ctx.restore();
    }

    // ────────────── ROW 1: SCORE ──────────────
    const innerPad = 14;
    const innerLeft = stripX + innerPad;
    const innerRight = stripX + stripW - innerPad;
    let currY = stripY + 24;

    // Team color dot
    ctx.fillStyle = battingColor;
    ctx.beginPath();
    ctx.arc(innerLeft + 6, currY, 6, 0, Math.PI * 2);
    ctx.fill();

    // Team Abbr
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.font = "bold 18px Outfit, sans-serif";
    ctx.fillText(battingTeam.name.substring(0, 4).toUpperCase(), innerLeft + 18, currY + 1);

    // Score (big)
    ctx.font = "bold 28px ui-monospace, monospace";
    const scoreText = inn.totalRuns + "-" + inn.wickets;
    const scoreW = ctx.measureText(scoreText).width;
    const scoreStartX = innerLeft + 76;
    ctx.fillText(scoreText, scoreStartX, currY + 2);

    // Overs (accent)
    ctx.fillStyle = accent;
    ctx.font = "bold 16px ui-monospace, monospace";
    ctx.fillText("(" + formatOvers(inn.balls) + ")", scoreStartX + scoreW + 8, currY + 2);

    // CRR
    ctx.textAlign = "right";
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "bold 12px sans-serif";
    const crr = inn.balls > 0 ? ((inn.totalRuns / inn.balls) * 6).toFixed(2) : "0.00";
    ctx.fillText("CRR " + crr, innerRight, currY + 2);

    // ────────────── DIVIDER ──────────────
    currY += 24;
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(stripX, currY);
    ctx.lineTo(stripX + stripW, currY);
    ctx.stroke();

    // ────────────── ROW 2: BATSMAN & BOWLER ──────────────
    currY += 22;
    const colW = stripW / 2;
    const midX = stripX + colW;
    const colPad = 12;

    // Striker (Left Col)
    if (striker) {
      ctx.textAlign = "left";
      ctx.fillStyle = accent;
      ctx.font = "bold 13px sans-serif";
      const nameTxt = "▶ " + striker.name.toUpperCase();
      ctx.fillText(clipText(ctx, nameTxt, colW - colPad - 70), innerLeft, currY);

      ctx.fillStyle = "#fff";
      ctx.font = "bold 14px ui-monospace, monospace";
      ctx.textAlign = "right";
      ctx.fillText(`${striker.runs}* (${striker.balls})`, midX - colPad, currY);
    }

    // Vertical Divider
    ctx.beginPath();
    ctx.moveTo(midX, currY - 14);
    ctx.lineTo(midX, currY + 14);
    ctx.stroke();

    // Bowler (Right Col)
    if (bowler) {
      ctx.textAlign = "left";
      ctx.fillStyle = bowlingColor;
      ctx.font = "bold 13px sans-serif";
      const bwlNameTxt = "⚡ " + bowler.name.toUpperCase();
      ctx.fillText(clipText(ctx, bwlNameTxt, colW - colPad - 80), midX + colPad, currY);

      ctx.fillStyle = "#fff";
      ctx.font = "bold 14px ui-monospace, monospace";
      ctx.textAlign = "right";
      ctx.fillText(`${bowler.wickets}-${bowler.runs} (${formatOvers(bowler.balls)})`, innerRight, currY);
    }

    // ────────────── DIVIDER ──────────────
    currY += 20;
    ctx.beginPath();
    ctx.moveTo(stripX, currY);
    ctx.lineTo(stripX + stripW, currY);
    ctx.stroke();

    // ────────────── ROW 3: PILLS & TARGET ──────────────
    currY += 24;
    ctx.textAlign = "left";
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "900 11px sans-serif";
    ctx.fillText("THIS OVER", innerLeft, currY);

    const pills = currentOverBalls(inn);
    const pillSize = 16, pillGap = 3;
    if (pills.length === 0) {
      ctx.fillStyle = "#525252";
      ctx.font = "italic 11px sans-serif";
      ctx.fillText("Start of over", innerLeft + 70, currY);
    } else {
      let px = innerLeft + 70 + pillSize / 2, py = currY;
      pills.forEach(b => {
        drawBallPill(ctx, px, py, pillSize, b);
        px += pillSize + pillGap;
      });
    }

    // Target / Chase Bar
    if (challengeConfig || target) {
      ctx.textAlign = "right";
      if (challengeConfig) {
        let needText = "";
        const c = challengeConfig;
        if (c.type === 'target_runs') {
          needText = "NEED " + Math.max(0, c.targetValue - inn.totalRuns) + " RUNS";
        } else if (c.type === 'target_sixes') {
          const currentSixes = inn.batsmen.reduce((sum, b) => sum + b.sixes, 0);
          needText = "NEED " + Math.max(0, c.targetValue - currentSixes) + " SIXES";
        } else if (c.type === 'target_wickets') {
          needText = "NEED " + Math.max(0, c.targetValue - inn.wickets) + " WKTS";
        }
        ctx.fillStyle = "#fff";
        ctx.font = "bold 12px ui-monospace, monospace";
        ctx.fillText(needText, innerRight, currY);
      } else if (target) {
        const need = Math.max(0, target - inn.totalRuns);
        const ballsLeft = m.overs * 6 - inn.balls;
        ctx.fillStyle = "#fff";
        ctx.font = "bold 12px ui-monospace, monospace";
        ctx.fillText("NEED " + need + " in " + ballsLeft + "b", innerRight, currY - 4);

        // Chase progress bar
        const pbW = 100;
        const pbH = 2;
        const pbX = innerRight - pbW;
        const pbY = currY + 6;
        const chasePct = Math.max(0, Math.min(1, inn.totalRuns / Math.max(1, target)));
        
        ctx.fillStyle = "rgba(255,255,255,0.10)";
        roundRect(ctx, pbX, pbY, pbW, pbH, 1); ctx.fill();
        if (chasePct > 0) {
          ctx.fillStyle = accent;
          roundRect(ctx, pbX, pbY, Math.max(pbH, pbW * chasePct), pbH, 1); ctx.fill();
        }
      }
    }
  });

  // Dedicated commentary lane above the scorecard. This reserved band keeps
  // spoken-event text away from score, batter, bowler and chase information.
  if (_lastBallState?.commentary?.text) {
    const elapsed = Date.now() - Number(_lastBallState.ts || 0);
    const holdMs = 5000;
    if (elapsed > holdMs) {
      lastBallState.set(null);
    } else {
      withCtx(ctx, () => {
        const panelX = 12;
        const panelW = W - 24;
        const panelH = 72;
        const panelY = H - 155 - 12 - panelH - 12;
        const alpha = elapsed < 250 ? elapsed / 250
          : (elapsed > holdMs - 600 ? (holdMs - elapsed) / 600 : 1);
        ctx.globalAlpha = Math.max(0, Math.min(1, alpha));

        const bg = ctx.createLinearGradient(panelX, panelY, panelX + panelW, panelY);
        bg.addColorStop(0, 'rgba(7, 17, 31, 0.94)');
        bg.addColorStop(1, 'rgba(15, 35, 55, 0.88)');
        ctx.fillStyle = bg;
        roundRect(ctx, panelX, panelY, panelW, panelH, 12); ctx.fill();
        ctx.strokeStyle = 'rgba(56,189,248,0.35)';
        ctx.lineWidth = 1;
        roundRect(ctx, panelX, panelY, panelW, panelH, 12); ctx.stroke();
        ctx.fillStyle = _lastBallState.color || '#38bdf8';
        roundRect(ctx, panelX, panelY, 5, panelH, 3); ctx.fill();

        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#7dd3fc';
        ctx.font = '900 10px Outfit, sans-serif';
        ctx.fillText('LIVE COMMENTARY', panelX + 16, panelY + 10);

        const rawText = String(_lastBallState.commentary.text)
          .replace(/^(?:Aakash Chopra|Ravi Shastri|Sidhu) style:\s*/, '');
        const maxTextW = panelW - 32;
        const words = rawText.split(/\s+/);
        const lines = [''];
        ctx.font = '700 13px Outfit, sans-serif';
        for (const word of words) {
          const current = lines[lines.length - 1];
          const candidate = current ? `${current} ${word}` : word;
          if (ctx.measureText(candidate).width <= maxTextW || !current) {
            lines[lines.length - 1] = candidate;
          } else if (lines.length < 2) {
            lines.push(word);
          } else {
            lines[1] = clipText(ctx, `${lines[1]} ${word}`, maxTextW);
            break;
          }
        }
        ctx.fillStyle = '#f8fafc';
        lines.slice(0, 2).forEach((line, index) => {
          ctx.fillText(clipText(ctx, line, maxTextW), panelX + 16, panelY + 28 + index * 17);
        });
      });
    }
  }
}
